import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  IconButton,
  CircularProgress,
  Chip,
  Button,
  Tooltip,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import SmartToyIcon from "@mui/icons-material/SmartToy";
import RefreshIcon from "@mui/icons-material/Refresh";
import api from "../../api/client";
import { useSnackbar } from "../../providers/SnackbarProvider";
import { colors, radius, shadow } from "../../theme/tokens";

/**
 * LLMSettingsPanel — swaps into the sidebar drawer (in place of the normal menu)
 * when a Super Admin opens "LLM Settings" from the profile dropdown.
 *
 * Backend contract:
 *   GET  /LLMConfig -> { status, active_model_id, models: [{ id, model_name, model_id, status }] }
 *   PUT  /LLMConfig  { model_id } -> { status, message, active_model_id }
 *   Errors: 401 (token), 403 (not Super Admin), 422 (bad model id), 500 (switch failure)
 *
 * The X icon in the header calls onClose() to return to the normal sidebar.
 */
const LLMSettingsPanel = ({ onClose }) => {
  const { showSnackbar } = useSnackbar();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [models, setModels] = useState([]);
  const [activeModelId, setActiveModelId] = useState(null);
  const [switchingId, setSwitchingId] = useState(null); // model_id mid-switch

  const fetchConfig = async () => {
    setLoading(true);
    setError(false);
    try {
      const res = await api.get("/LLMConfig");
      if (res?.data?.status === "success") {
        setModels(res.data.models || []);
        setActiveModelId(res.data.active_model_id || null);
      } else {
        setError(true);
        showSnackbar("error", res?.data?.message || "Failed to load LLM models");
      }
    } catch (e) {
      setError(true);
      const status = e?.response?.status;
      showSnackbar(
        "error",
        e?.response?.data?.message ||
          (status === 403
            ? "Only a Super Admin can manage LLM settings"
            : "Failed to load LLM models")
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchConfig();
  }, []);

  const handleSwitch = async (modelId) => {
    if (!modelId || modelId === activeModelId || switchingId) return;
    setSwitchingId(modelId);
    try {
      const res = await api.put("/LLMConfig", { model_id: modelId });
      if (res?.data?.status === "success") {
        setActiveModelId(res.data.active_model_id || modelId);
        showSnackbar("success", res.data.message || "Active model updated");
      } else {
        showSnackbar("error", res?.data?.message || "Failed to switch model");
      }
    } catch (e) {
      const status = e?.response?.status;
      showSnackbar(
        "error",
        e?.response?.data?.message ||
          (status === 403
            ? "Only a Super Admin can switch the model"
            : status === 422
            ? "Invalid model selected"
            : "Failed to switch model")
      );
    } finally {
      setSwitchingId(null);
    }
  };

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        height: "100%",
        overflow: "hidden",
      }}
    >
      {/* Header */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          px: 2,
          py: 2,
          mt: "10px",
          borderBottom: `1px solid ${colors.line}`,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, minWidth: 0 }}>
          <SmartToyIcon sx={{ color: colors.primary.main, fontSize: 24 }} />
          <Box sx={{ minWidth: 0 }}>
            <Typography
              sx={{
                fontSize: "16px",
                fontWeight: 700,
                color: colors.ink,
                lineHeight: 1.2,
                whiteSpace: "nowrap",
              }}
            >
              LLM Settings
            </Typography>
            <Typography sx={{ fontSize: "11px", color: colors.muted, whiteSpace: "nowrap" }}>
              Choose the active model
            </Typography>
          </Box>
        </Box>
        <Tooltip title="Back to menu" arrow placement="bottom">
          <IconButton size="small" onClick={onClose} sx={{ color: colors.muted }}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </Box>

      {/* Body */}
      <Box
        sx={{
          flex: 1,
          minHeight: 0,
          overflowY: "auto",
          overflowX: "hidden",
          px: 1.5,
          py: 1.5,
          "&::-webkit-scrollbar": { width: "4px" },
          "&::-webkit-scrollbar-thumb": {
            backgroundColor: colors.primary.main,
            borderRadius: "4px",
          },
        }}
      >
        {loading ? (
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              py: 6,
            }}
          >
            <CircularProgress size={26} sx={{ color: colors.primary.main }} />
          </Box>
        ) : error ? (
          <Box sx={{ textAlign: "center", py: 5 }}>
            <Typography sx={{ fontSize: "13px", color: colors.muted, mb: 1.5 }}>
              Couldn't load the models.
            </Typography>
            <Button
              startIcon={<RefreshIcon />}
              size="small"
              variant="outlined"
              onClick={fetchConfig}
              sx={{ textTransform: "none", color: colors.primary.main, borderColor: colors.primary.main }}
            >
              Retry
            </Button>
          </Box>
        ) : models.length === 0 ? (
          <Typography
            sx={{ fontSize: "13px", color: colors.muted, textAlign: "center", py: 5 }}
          >
            No models available.
          </Typography>
        ) : (
          <Box sx={{ display: "flex", flexDirection: "column", gap: 1.25 }}>
            {models.map((m) => {
              const isActive = m.model_id === activeModelId;
              const isSwitching = switchingId === m.model_id;
              const disabled = isActive || Boolean(switchingId);

              return (
                <Box
                  key={m.model_id ?? m.id}
                  onClick={() => handleSwitch(m.model_id)}
                  sx={{
                    cursor: disabled ? "default" : "pointer",
                    borderRadius: `${radius.card}px`,
                    border: `1.5px solid ${isActive ? colors.primary.main : colors.line}`,
                    backgroundColor: isActive ? "#f0f9ff" : colors.surface,
                    boxShadow: isActive ? shadow.sm : "none",
                    p: 1.5,
                    transition: "all 0.2s ease",
                    opacity: switchingId && !isSwitching ? 0.55 : 1,
                    pointerEvents: switchingId ? "none" : "auto",
                    "&:hover": {
                      borderColor: colors.primary.main,
                      backgroundColor: "#f0f9ff",
                    },
                  }}
                >
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "flex-start",
                      justifyContent: "space-between",
                      gap: 1,
                    }}
                  >
                    <Box sx={{ minWidth: 0 }}>
                      <Typography
                        sx={{
                          fontSize: "13.5px",
                          fontWeight: 600,
                          color: colors.body,
                          whiteSpace: "nowrap",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                        }}
                      >
                        {m.model_name}
                      </Typography>
                      <Typography
                        sx={{
                          fontSize: "11px",
                          color: colors.muted,
                          fontFamily: "monospace",
                          whiteSpace: "nowrap",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                        }}
                      >
                        {m.model_id}
                      </Typography>
                    </Box>
                    {isSwitching ? (
                      <CircularProgress
                        size={18}
                        sx={{ color: colors.primary.main, flexShrink: 0, mt: "2px" }}
                      />
                    ) : isActive ? (
                      <CheckCircleIcon
                        sx={{ color: colors.primary.main, fontSize: 20, flexShrink: 0 }}
                      />
                    ) : (
                      <Box
                        sx={{
                          width: 18,
                          height: 18,
                          mt: "1px",
                          borderRadius: "50%",
                          border: `2px solid ${colors.line}`,
                          flexShrink: 0,
                        }}
                      />
                    )}
                  </Box>
                  <Box sx={{ mt: 1 }}>
                    <Chip
                      label={isActive ? "Active" : "Inactive"}
                      size="small"
                      sx={{
                        height: 20,
                        fontSize: "10.5px",
                        fontWeight: 700,
                        borderRadius: `${radius.sm}px`,
                        backgroundColor: isActive ? colors.success.soft : colors.line,
                        color: isActive ? colors.success.dark : colors.muted,
                      }}
                    />
                  </Box>
                </Box>
              );
            })}
          </Box>
        )}
      </Box>

      {/* Footer hint */}
      <Box sx={{ px: 2, py: 1.5, borderTop: `1px solid ${colors.line}` }}>
        <Typography sx={{ fontSize: "10.5px", color: colors.muted, lineHeight: 1.4 }}>
          Selecting a model switches it for the whole platform.
        </Typography>
      </Box>
    </Box>
  );
};

export default LLMSettingsPanel;
